import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  AlertCircle,
  Camera,
  CheckCircle2,
  ChevronDown,
  Copy,
  FlaskConical,
  History,
  Inbox,
  LoaderCircle,
  LogOut,
  ReceiptText,
  Ticket,
  User,
  UserCog,
} from "lucide-react";
import { ArrowRight } from "./Icons";
import { useSession, maskPhone } from "./account/session";
import { tokenStore, type ApiUser } from "./account/api";
import { AvatarEditor } from "./account/AvatarEditor";
import {
  useResource,
  useCursorPagination,
  useAccountLocation,
  accountDateFilter,
  type AccountTab,
  type AccountLocation,
} from "./account/resources";
import {
  createRedeemer,
  describePasswordError,
  fetchCredits,
  fetchRecordsPage,
  fetchResearchPage,
  formatCredits,
  formatCreditsDelta,
  formatDateTime,
  RECORDS_PAGE_SIZE,
  newerCredits,
  normalizeNickname,
  passwordByteLength,
  recordTypeLabel,
  resetPassword,
  sendPasswordResetCode,
  setFirstPassword,
  updateCurrentUserProfile,
  type CreditsResponse,
  type BillingRecord,
  type RecordsFilter,
  type RedeemSuccess,
  type RedeemOutcome,
  type ResearchUsageSummary,
} from "./account/data";

const MODULES: [AccountTab, string, typeof UserCog][] = [
  ["settings", "账号设置", UserCog],
  ["billing", "研值明细", ReceiptText],
  ["redeem", "兑换码", Ticket],
];
function Card({
  icon: Icon,
  title,
  desc,
  children,
}: {
  icon: typeof User;
  title: string;
  desc?: string;
  children: ReactNode;
}) {
  return (
    <section className="auth-glass acct-card">
      <div className="acct-card-head">
        <div>
          <h2>
            <span className="acct-card-icon" aria-hidden="true">
              <Icon size={16} />
            </span>
            {title}
          </h2>
          {desc && <p className="acct-card-desc">{desc}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}
function Loading({ label }: { label: string }) {
  return (
    <p className="acct-state acct-loading" role="status">
      <LoaderCircle size={18} className="acct-spinner" aria-hidden="true" />
      <span>{label}</span>
    </p>
  );
}
function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="acct-state acct-empty" role="status">
      <Inbox size={18} aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}
function Stage({ children }: { children: ReactNode }) {
  return (
    <section className="acct-stage">
      <div className="container acct-inner">{children}</div>
    </section>
  );
}
function Failure({ error, retry, busy = false }: { error: string; retry: () => void; busy?: boolean }) {
  return (
    <div className="acct-state acct-resource-error">
      <AlertCircle size={18} aria-hidden="true" />
      <p className="auth-error" role="alert">
        {error}
      </p>
      <button type="button" className="auth-outline" disabled={busy} onClick={retry}>
        重试
      </button>
    </div>
  );
}
function SessionGate({ notice = "" }: { notice?: string }) {
  const { status, retrySession } = useSession();
  return status === "checking" ? (
    <Loading label="正在确认登录状态…" />
  ) : status === "unavailable" ? (
    <div className="acct-gate auth-glass acct-card">
      <p>暂时无法确认登录状态，请检查网络后重试。</p>
      <button className="auth-outline" type="button" onClick={retrySession}>
        重新确认登录状态
      </button>
    </div>
  ) : (
    <div className="acct-gate auth-glass acct-card">
      {notice && <p role="status">{notice}</p>}
      <p>登录后查看研值余额、使用明细并使用兑换码。</p>
      <a className="auth-primary acct-gate-cta" href="/login">
        登录 / 注册 <ArrowRight size={17} />
      </a>
    </div>
  );
}
export function AccountPage() {
  const { status, user } = useSession();
  const [notice, setNotice] = useState("");
  useEffect(() => {
    if (status === "authenticated") setNotice("");
  }, [status]);
  return (
    <Stage>
      <p className="auth-kicker">Account</p>
      <h1 className="acct-title">个人中心</h1>
      {status === "authenticated" && user ? (
        <AuthenticatedAccount
          key={`${user.id}:${tokenStore.getSessionVersion()}`}
          user={user}
          onPasswordReset={() => setNotice("密码已修改，请重新登录。")}
        />
      ) : (
        <SessionGate notice={notice} />
      )}
    </Stage>
  );
}
function NavRail({
  module,
  onSelect,
}: {
  module: AccountTab;
  onSelect: (next: AccountTab) => void;
}) {
  const { logout } = useSession();
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    if (!confirming) return;
    const timer = window.setTimeout(() => setConfirming(false), 4000);
    return () => window.clearTimeout(timer);
  }, [confirming]);
  return (
    <aside className="acct-rail">
      <nav aria-label="个人中心模块">
        {MODULES.map(([key, label, Icon]) => (
          <button
            key={key}
            type="button"
            aria-current={module === key ? "page" : undefined}
            onClick={() => onSelect(key)}
          >
            <Icon size={18} aria-hidden="true" />
            {label}
          </button>
        ))}
      </nav>
      <div className="acct-rail-foot">
        <button
          className="acct-logout-button"
          type="button"
          onClick={() => {
            if (confirming) void logout();
            else setConfirming(true);
          }}
        >
          <LogOut size={18} aria-hidden="true" />
          {confirming ? "再次点击确认退出" : "退出登录"}
        </button>
        <a className="acct-help-link" href="/download#contact">
          帮助与反馈
        </a>
      </div>
    </aside>
  );
}
function IdentityBanner({
  user,
  credits,
  loading,
  onRedeem,
  onAvatar,
}: {
  user: ApiUser;
  credits: CreditsResponse | null;
  loading: boolean;
  onRedeem: () => void;
  onAvatar: () => void;
}) {
  const name =
    user.nick_name?.trim() ||
    (user.phone ? maskPhone(user.phone) : "未命名用户");
  return (
    <section className="auth-glass acct-banner" aria-label="账号信息">
      <div className="acct-identity">
        <button
          className="acct-avatar"
          type="button"
          aria-label="更换头像"
          onClick={onAvatar}
        >
          {user.avatar_url ? (
            <img src={user.avatar_url} alt="" />
          ) : (
            <User size={28} />
          )}
          <span className="acct-avatar-hover">
            <Camera size={18} />
          </span>
        </button>
        <div className="acct-identity-text">
          <div className="acct-name-row">
            <p className="acct-name">{name}</p>
            {credits && (
              <span className="acct-badge">
                {credits.unlimited ? "无限额度账号" : "普通账号"}
              </span>
            )}
          </div>
          {user.phone && <p className="acct-phone">{maskPhone(user.phone)}</p>}
        </div>
      </div>
      <div className="acct-readouts">
        <div className="acct-chip">
          <p>研值余额</p>
          <strong>
            {credits
              ? credits.unlimited
                ? "无限额度"
                : `${formatCredits(credits.balance)} 研值`
              : loading
                ? "正在查询…"
                : "暂不可用"}
          </strong>
        </div>
        <button
          className="auth-primary acct-redeem-cta"
          type="button"
          onClick={onRedeem}
        >
          <Ticket size={16} />
          使用兑换码
        </button>
      </div>
    </section>
  );
}
function AuthenticatedAccount({
  user,
  onPasswordReset,
}: {
  user: ApiUser;
  onPasswordReset: () => void;
}) {
  const [location, navigate] = useAccountLocation();
  const reconciled = useRef<CreditsResponse | null>(null);
  const credits = useResource(
    (signal) =>
      fetchCredits(signal).then((data) => {
        signal.throwIfAborted();
        const next = newerCredits(reconciled.current, data);
        reconciled.current = next;
        return next;
      }),
    [user.id],
  );
  const [billingRevision, setBillingRevision] = useState(0);

  const go = (tab: AccountTab) => navigate({ tab });
  const redeemed = (result: RedeemSuccess) => {
    const next = newerCredits(reconciled.current, {
      balance: result.balance,
      balance_revision: result.balanceRevision,
      unlimited: false,
    });
    reconciled.current = next;
    credits.update(next);
    credits.reload();
    setBillingRevision((v) => v + 1);
  };
  return (
    <>
      <IdentityBanner
        user={user}
        credits={credits.data}
        loading={credits.loading}
        onRedeem={() => go("redeem")}
        onAvatar={() => go("settings")}
      />
      {credits.error && (
        <Failure
          error={
            credits.data
              ? "余额暂未更新，请重试。"
              : "余额查询失败，其他功能仍可使用。"
          }
          retry={credits.reload}
        />
      )}
      <div className="acct-body">
        <NavRail module={location.tab} onSelect={go} />
        <div className="acct-content">
          <div hidden={location.tab !== "settings"}>
            <SettingsModule
              user={user}
              active={location.tab === "settings"}
              onPasswordReset={onPasswordReset}
            />
          </div>
          {location.tab === "billing" && (
            <BillingModule
              location={location}
              navigate={navigate}
              revision={billingRevision}
            />
          )}
          <div hidden={location.tab !== "redeem"}>
            {location.tab === "redeem" && (
              <RedeemModule
                credits={credits.data}
                onRedeemed={redeemed}
                viewRecord={(id) =>
                  navigate({
                    tab: "billing",
                    billingView: "records",
                    range: "all",
                    type: "redeem",
                    recordId: id,
                  })
                }
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
function SettingsModule({
  user,
  active,
  onPasswordReset,
}: {
  user: ApiUser;
  active: boolean;
  onPasswordReset: () => void;
}) {
  const { updateUser } = useSession();
  const [nickname, setNickname] = useState(user.nick_name ?? ""),
    [avatar, setAvatar] = useState(user.avatar_url ?? "");
  const [file, setFile] = useState<File | null>(null),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [error, setError] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const controller = useRef(new AbortController());
  useEffect(() => {
    const c = new AbortController();
    controller.current = c;
    return () => c.abort();
  }, []);
  const dirty =
    nickname.trim() !== (user.nick_name ?? "") ||
    avatar !== (user.avatar_url ?? "");
  useEffect(() => {
    if (!dirty) return;
    const leave = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", leave);
    return () => window.removeEventListener("beforeunload", leave);
  }, [dirty]);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !dirty || file) return;
    setError("");
    let name: string;
    try {
      name = normalizeNickname(nickname);
    } catch (err) {
      setError(err instanceof Error ? err.message : "昵称格式不正确");
      return;
    }
    setBusy(true);
    const signal = controller.current.signal;
    try {
      const next = await updateCurrentUserProfile({
        nick_name: name,
        avatar_url: avatar,
      });
      if (signal.aborted) return;
      updateUser({
        id: next.id,
        nick_name: next.nick_name,
        avatar_url: next.avatar_url,
      });
      setNickname(next.nick_name ?? "");
      setAvatar(next.avatar_url ?? "");
      setMessage("已保存");
    } catch {
      if (!signal.aborted) setError("资料保存失败，请稍后重试。");
    } finally {
      if (!signal.aborted) setBusy(false);
    }
  };
  return (
    <section
      className="auth-glass acct-card acct-settings-card"
      aria-label="账号设置"
    >
      <form className="acct-form" onSubmit={(e) => void save(e)} noValidate>
        <div className="acct-profile-grid">
          <div className="acct-avatar-pick">
            <div className="acct-avatar-large">
              {avatar ? (
                <img src={avatar} alt="头像预览" />
              ) : (
                <User size={32} />
              )}
            </div>
            <div>
              <input
                ref={input}
                type="file"
                className="sr-only"
                accept="image/png,image/jpeg,image/gif,image/webp"
                aria-label="选择头像文件"
                disabled={busy}
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  setMessage("");
                  setError("");
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                className="auth-outline"
                disabled={busy}
                onClick={() => input.current?.click()}
              >
                选择头像
              </button>
              <p className="acct-hint">
                原图最大 10 MB，裁剪后自动压缩为静态头像。
              </p>
            </div>
          </div>
          <div className="acct-field">
            <label htmlFor="account-nickname">昵称</label>
            <input
              id="account-nickname"
              value={nickname}
              autoComplete="nickname"
              placeholder="请输入 1–32 个字符"
              disabled={busy}
              aria-invalid={error ? true : undefined}
              onChange={(e) => {
                setNickname(Array.from(e.target.value).slice(0, 32).join(""));
                setError("");
                setMessage("");
              }}
            />
            <p className="acct-hint">
              {Array.from(nickname).length}/32{dirty ? " · 尚未保存" : ""}
            </p>
          </div>
        </div>
        {file && (
          <AvatarEditor
            file={file}
            onCancel={() => setFile(null)}
            onSave={(url) => {
              setAvatar(url);
              setFile(null);
              setMessage("");
            }}
          />
        )}
        {error && (
          <p className="auth-error" role="alert">
            {error}
          </p>
        )}
        {(dirty || busy || message) && (
          <div className="acct-profile-save">
            <p className="acct-saved" role="status">
              {message}
            </p>
            {(dirty || busy) && (
              <button
                type="submit"
                className="auth-primary"
                disabled={busy || !!file}
              >
                {busy ? "保存中…" : "保存"}
              </button>
            )}
          </div>
        )}
      </form>
      <dl className="acct-facts">
        <div>
          <dt>手机号</dt>
          <dd>{user.phone ? maskPhone(user.phone) : "未绑定"}</dd>
        </div>
        <div>
          <dt>账号 ID</dt>
          <dd>
            <CopyText value={user.id} />
          </dd>
        </div>
      </dl>
      {active && (
        <PasswordSettings
          key={user.has_password ? "reset" : "first"}
          user={user}
          onReset={onPasswordReset}
        />
      )}
    </section>
  );
}
function PasswordSettings({
  user,
  onReset,
}: {
  user: ApiUser;
  onReset: () => void;
}) {
  const { updateUser, logout } = useSession();
  const [open, setOpen] = useState(false),
    [password, setPassword] = useState(""),
    [confirm, setConfirm] = useState(""),
    [code, setCode] = useState(""),
    [busy, setBusy] = useState(false),
    [sending, setSending] = useState(false),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [cooldown, setCooldown] = useState(0),
    [show, setShow] = useState(false);
  const controller = useRef(new AbortController());
  useEffect(() => {
    const c = new AbortController();
    controller.current = c;
    return () => c.abort();
  }, []);
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(
      () => setCooldown((v) => Math.max(0, v - 1)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [cooldown > 0]);
  const send = async () => {
    if (!user.phone || sending || cooldown) return;
    setSending(true);
    setError("");
    const signal = controller.current.signal;
    try {
      await sendPasswordResetCode(user.phone, signal);
      if (signal.aborted) return;
      setCooldown(60);
      setMessage("验证码请求已提交，请查看绑定手机号。");
    } catch (e) {
      if (!signal.aborted) {
        const problem = describePasswordError(e);
        setError(problem.message);
        setCooldown(problem.retryAfterSeconds ?? 0);
      }
    } finally {
      if (!signal.aborted) setSending(false);
    }
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const bytes = passwordByteLength(password);
    if (bytes < 8 || bytes > 72) {
      setError("密码长度需为 8–72 字节。");
      return;
    }
    if (password !== confirm) {
      setError("两次输入的密码不一致。");
      return;
    }
    if (user.has_password && !/^\d{6}$/.test(code)) {
      setError("请输入 6 位验证码");
      return;
    }
    setBusy(true);
    setError("");
    const signal = controller.current.signal;
    try {
      if (user.has_password) {
        await resetPassword(user.phone ?? "", code, password, signal);
        if (signal.aborted) return;
        onReset();
        await logout();
      } else {
        await setFirstPassword(password);
        if (signal.aborted) return;
        updateUser({ id: user.id, has_password: true });
        setMessage("密码已设置。");
      }
      setPassword("");
      setConfirm("");
      setCode("");
    } catch (e) {
      if (!signal.aborted) setError(describePasswordError(e).message);
    } finally {
      if (!signal.aborted) setBusy(false);
    }
  };
  return (
    <section className="acct-password-settings" aria-label="登录密码">
      <div className="acct-password-row">
        <span className="acct-password-label">登录密码</span>
        <div className="acct-password-actions">
          <span className="acct-password-state">
            {user.has_password ? "已设置" : "未设置"}
          </span>
          <button
            type="button"
            className="auth-outline"
            aria-expanded={open}
            aria-controls="account-password-form"
            disabled={busy || (user.has_password && !user.phone)}
            onClick={() => setOpen(true)}
          >
            {user.has_password ? "修改密码" : "设置密码"}
          </button>
        </div>
      </div>
      {user.has_password && !user.phone && (
        <p className="acct-hint">请联系客服完成手机号绑定后再修改密码。</p>
      )}
      {open && (
        <form
          id="account-password-form"
          className="acct-form acct-narrow"
          noValidate
          onSubmit={(e) => void submit(e)}
        >
          {user.has_password && (
            <>
              <p className="acct-hint">
                验证手机号 {maskPhone(user.phone ?? "")}
                ，修改成功后需要重新登录。
              </p>
              <div className="acct-field">
                <label htmlFor="account-reset-code">手机验证码</label>
                <div className="acct-code-row">
                  <input
                    id="account-reset-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  />
                  <button
                    type="button"
                    className="auth-outline"
                    disabled={sending || cooldown > 0 || busy}
                    onClick={() => void send()}
                  >
                    {sending
                      ? "发送中…"
                      : cooldown
                        ? `${cooldown}s 后重发`
                        : "获取验证码"}
                  </button>
                </div>
              </div>
            </>
          )}
          <div className="acct-field">
            <label htmlFor="account-password">
              {user.has_password ? "新密码" : "设置密码"}
            </label>
            <input
              id="account-password"
              type={show ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              disabled={busy}
              placeholder="8–72 字节"
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
            />
          </div>
          <div className="acct-field">
            <label htmlFor="account-password-confirm">确认密码</label>
            <input
              id="account-password-confirm"
              type={show ? "text" : "password"}
              autoComplete="new-password"
              value={confirm}
              disabled={busy}
              onChange={(e) => {
                setConfirm(e.target.value);
                setError("");
              }}
            />
          </div>
          <label className="acct-check">
            <input
              type="checkbox"
              checked={show}
              onChange={(e) => setShow(e.target.checked)}
            />
            显示密码
          </label>
          {error && (
            <p className="auth-error" role="alert">
              {error}
            </p>
          )}
          <p className="acct-saved" role="status">
            {message}
          </p>
          <div className="acct-form-actions">
            <button
              type="button"
              className="auth-outline"
              disabled={busy}
              onClick={() => {
                setOpen(false);
                setPassword("");
                setConfirm("");
                setCode("");
                setError("");
                setMessage("");
              }}
            >
              取消
            </button>
            <button type="submit" className="auth-primary" disabled={busy}>
              {busy
                ? "保存中…"
                : user.has_password
                  ? "确认修改并重新登录"
                  : "保存密码"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
function CopyText({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false),
    [failed, setFailed] = useState(false);
  return (
    <>
      <button
        type="button"
        className="acct-copy"
        onClick={() => {
          if (!navigator.clipboard) {
            setFailed(true);
            return;
          }
          void navigator.clipboard
            .writeText(value)
            .then(() => {
              setCopied(true);
              setFailed(false);
            })
            .catch(() => setFailed(true));
        }}
        aria-label={label ?? `复制 ${value}`}
      >
        <Copy size={14} aria-hidden="true" />
        {copied ? "已复制" : (label ?? value)}
      </button>
      {failed && <span role="status">请手动复制：{value}</span>}
    </>
  );
}
function Feedback({
  record,
  topic,
}: {
  record?: BillingRecord;
  topic?: string;
}) {
  const [summary, setSummary] = useState("");
  const copy = () => {
    const text = [
      `Sciland 问题反馈：${topic ?? "研值记录"}`,
      record ? `流水 ID：${record.id}` : "",
      record?.research_id ? `研究 ID：${record.research_id}` : "",
      record
        ? `发生时间：${formatDateTime(record.created_at)}`
        : `反馈时间：${formatDateTime(new Date().toISOString())}`,
    ]
      .filter(Boolean)
      .join("\n");
    setSummary(text);
    void navigator.clipboard?.writeText(text).catch(() => {});
  };
  return (
    <details className="acct-feedback-help">
      <summary>需要帮助或反馈？</summary>
      <p className="acct-hint">
        联系
        support@isoplex.cn，并提供时间和相关记录。请勿发送密码、验证码或完整兑换码。
      </p>
      <button type="button" className="auth-outline" onClick={copy}>
        生成反馈摘要
      </button>
      {summary && (
        <pre className="acct-feedback-summary" role="status">
          {summary}
        </pre>
      )}
      <a className="acct-text-link" href="mailto:support@isoplex.cn">
        联系客服
      </a>
    </details>
  );
}
function RecordDetailsContent({ record }: { record: BillingRecord }) {
  return (
    <>
      <dl className="acct-facts">
        <div>
          <dt>流水 ID</dt>
          <dd>
            <CopyText value={record.id} />
          </dd>
        </div>
        {record.research_id && (
          <div>
            <dt>研究 ID</dt>
            <dd>
              <CopyText value={record.research_id} />
            </dd>
          </div>
        )}
        <div>
          <dt>说明</dt>
          <dd>{record.description || "—"}</dd>
        </div>
        <div>
          <dt>研值变化</dt>
          <dd>{formatCreditsDelta(record.credits)}</dd>
        </div>
        <div>
          <dt>免扣研值</dt>
          <dd>{formatCredits(record.waived_credits)}</dd>
        </div>
        <div>
          <dt>变动后余额</dt>
          <dd>
            {formatCredits(record.balance_after)}
            {record.balance_changed === false ? "（余额未变动）" : ""}
          </dd>
        </div>
      </dl>
      <Feedback record={record} />
    </>
  );
}
function RecordDetails({ record }: { record: BillingRecord }) {
  return (
    <details className="acct-record-details">
      <summary>查看详情</summary>
      <RecordDetailsContent record={record} />
    </details>
  );
}
function DesktopRecordRow({
  record,
  highlighted,
}: {
  record: BillingRecord;
  highlighted: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const description = record.description || "—";
  return (
    <>
      <tr
        data-record-id={record.id}
        className={`acct-record-row${highlighted ? " is-highlighted" : ""}`}
      >
        <td>
          <div className="acct-record-time-type">
            <time dateTime={record.created_at}>{formatDateTime(record.created_at)}</time>
            <span className="acct-pill">{recordTypeLabel(record.type)}</span>
          </div>
        </td>
        <td className="acct-desc">
          <div className="acct-record-description">
            <span className="acct-record-label" title={description}>{description}</span>
            <button
              type="button"
              className="acct-record-toggle"
              aria-label={expanded ? "收起详情" : "查看详情"}
              aria-expanded={expanded}
              aria-controls={expanded ? detailsId : undefined}
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? "收起" : "详情"}
              <ChevronDown size={12} aria-hidden="true" />
            </button>
          </div>
        </td>
        <td className={`is-right acct-mono ${record.credits > 0 ? "is-positive" : ""}`}>
          {formatCreditsDelta(record.credits)}
          {record.waived_credits > 0 && (
            <span className="acct-record-waived">免扣 {formatCredits(record.waived_credits)}</span>
          )}
        </td>
        <td className="is-right acct-mono">{formatCredits(record.balance_after)}</td>
      </tr>
      {expanded && (
        <tr className="acct-record-expanded-row">
          <td colSpan={4} className="acct-record-expanded-cell">
            <div id={detailsId} className="acct-record-expanded">
              <RecordDetailsContent record={record} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
function Pagination({
  label,
  pageNumber,
  hasNext,
  loading,
  previous,
  next,
}: {
  label: string;
  pageNumber: number;
  hasNext: boolean;
  loading: boolean;
  previous: () => void;
  next: () => Promise<void>;
}) {
  const navigation = useRef<HTMLElement>(null);
  const previousPage = useRef(pageNumber);
  useEffect(() => {
    if (previousPage.current === pageNumber) return;
    previousPage.current = pageNumber;
    const panel = navigation.current?.closest<HTMLElement>('[role="tabpanel"]');
    const target = panel ?? navigation.current?.closest<HTMLElement>(".acct-card");
    panel?.focus({ preventScroll: true });
    target?.scrollIntoView({ block: "start", behavior: "instant" });
  }, [pageNumber]);
  return (
    <nav ref={navigation} className="acct-pagination" aria-label={label} aria-busy={loading}>
      <span className="acct-pagination-info" aria-live="polite" aria-atomic="true">
        第 {pageNumber} 页 · 每页 {RECORDS_PAGE_SIZE} 条
      </span>
      <div className="acct-pagination-actions">
        <button
          type="button"
          className="auth-outline"
          disabled={loading || pageNumber === 1}
          onClick={previous}
        >
          上一页
        </button>
        <button
          type="button"
          className="auth-outline"
          disabled={loading || !hasNext}
          onClick={() => void next()}
        >
          {loading ? "加载中…" : "下一页"}
        </button>
      </div>
    </nav>
  );
}
function RecordsList({
  filter,
  revision = 0,
  recordId = "",
  variant = "billing",
}: {
  filter: RecordsFilter;
  revision?: number;
  recordId?: string;
  variant?: "billing" | "redeem";
}) {
  const resource = useCursorPagination(
    (cursor, signal) => fetchRecordsPage(cursor, signal, filter),
    [filter, revision],
  );
  const records = resource.data?.records ?? [];
  const redeem = variant === "redeem";
  return (
    <>
      {resource.loading && !resource.data && (
        <Loading label={redeem ? "正在加载兑换记录…" : "正在加载研值明细…"} />
      )}
      {resource.error && (
        <Failure error={resource.error} retry={resource.reload} />
      )}
      {!resource.loading && !resource.error && !records.length && (
        <Empty>{redeem ? "暂无兑换记录" : "当前条件下暂无研值明细"}</Empty>
      )}
      {records.length > 0 && (
        <>
          <div className="acct-desktop-records">
            <table className="acct-table">
              <thead>
                <tr>
                  <th scope="col">时间 / 类型</th>
                  <th scope="col">说明</th>
                  <th scope="col" className="is-right">
                    研值变化
                  </th>
                  <th scope="col" className="is-right">
                    变动后余额
                  </th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <DesktopRecordRow
                    key={record.id}
                    record={record}
                    highlighted={record.id === recordId}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <ul className="acct-mobile-records">
            {records.map((record) => (
              <li
                key={record.id}
                data-record-id={record.id}
                className={record.id === recordId ? "is-highlighted" : ""}
              >
                <div className="acct-record-top">
                  <strong>
                    {record.description || recordTypeLabel(record.type)}
                  </strong>
                  <span
                    className={`acct-mono ${record.credits > 0 ? "is-positive" : ""}`}
                  >
                    {formatCreditsDelta(record.credits)} 研值
                  </span>
                </div>
                <p className="acct-hint">
                  {formatDateTime(record.created_at)} ·{" "}
                  {recordTypeLabel(record.type)}
                </p>
                <p className="acct-hint">
                  变动后余额 {formatCredits(record.balance_after)}
                  {record.waived_credits > 0
                    ? ` · 免扣 ${formatCredits(record.waived_credits)}`
                    : ""}
                </p>
                <RecordDetails record={record} />
              </li>
            ))}
          </ul>
        </>
      )}
      {resource.pageError && (
        <Failure error={resource.pageError} retry={() => void resource.next()} busy={resource.loading} />
      )}
      {resource.data && (records.length > 0 || resource.pageNumber > 1 || resource.hasNext) && (
        <Pagination label={redeem ? "兑换记录分页" : "研值流水分页"} {...resource} />
      )}
    </>
  );
}
function ResearchName({ item }: { item: ResearchUsageSummary }) {
  const name = item.research_name || `研究 ${item.research_id.slice(0, 8)}…`;
  return (
    <details className="acct-research-name">
      <summary title={name}>
        <span>{name}</span>
        <ChevronDown size={12} aria-hidden="true" />
      </summary>
      <div className="acct-research-details">
        <p>{name}</p>
        <p className="acct-mono">{item.research_id}</p>
        <CopyText value={item.research_id} label="复制研究 ID" />
      </div>
    </details>
  );
}
function ResearchRecordsButton({ id, onResearch }: { id: string; onResearch: (id: string) => void }) {
  return (
    <button className="acct-record-toggle" type="button" aria-label="查看消耗明细" onClick={() => onResearch(id)}>
      明细 <ArrowRight size={12} aria-hidden="true" />
    </button>
  );
}
function ResearchUsage({ onResearch }: { onResearch: (id: string) => void }) {
  const resource = useCursorPagination(
    (cursor, signal) => fetchResearchPage(cursor, signal),
    [],
  );
  const items = resource.data?.researchUsage ?? [];
  return (
    <>
      {resource.loading && !resource.data && (
        <Loading label="正在加载研究用量…" />
      )}
      {resource.error && (
        <Failure error={resource.error} retry={resource.reload} />
      )}
      {!resource.loading && !resource.error && !items.length && (
        <Empty>暂无研究用量</Empty>
      )}
      {items.length > 0 && (
        <>
          <div className="acct-desktop-research">
            <table className="acct-table acct-research-table">
              <thead>
                <tr>
                  <th scope="col">研究</th>
                  <th scope="col" className="is-right">调用</th>
                  <th scope="col" className="is-right">扣除研值</th>
                  <th scope="col" className="is-right">免扣研值</th>
                  <th scope="col">最后使用</th>
                  <th scope="col"><span className="sr-only">操作</span></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.research_id} className="acct-record-row">
                    <td><ResearchName item={item} /></td>
                    <td className="is-right acct-mono">{formatCredits(item.attempt_count)}</td>
                    <td className="is-right acct-mono">{formatCredits(item.credits_charged)}</td>
                    <td className="is-right acct-mono">{formatCredits(item.waived_credits)}</td>
                    <td className="acct-research-time"><time dateTime={item.last_used_at ?? item.first_used_at}>{formatDateTime(item.last_used_at ?? item.first_used_at)}</time></td>
                    <td className="is-right"><ResearchRecordsButton id={item.research_id} onResearch={onResearch} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="acct-research-list acct-mobile-research">
            {items.map((item) => (
              <li key={item.research_id}>
                <div className="acct-record-top">
                  <ResearchName item={item} />
                  <span className="acct-mono" aria-label={`实际扣除 ${formatCredits(item.credits_charged)} 研值`}>{formatCredits(item.credits_charged)} 研值</span>
                </div>
                <div className="acct-research-footer">
                  <div>
                    <p className="acct-hint">调用 {formatCredits(item.attempt_count)} 次 · 免扣 {formatCredits(item.waived_credits)} 研值</p>
                    <p className="acct-hint"><time dateTime={item.last_used_at ?? item.first_used_at}>{formatDateTime(item.last_used_at ?? item.first_used_at)}</time></p>
                  </div>
                  <ResearchRecordsButton id={item.research_id} onResearch={onResearch} />
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {resource.pageError && (
        <Failure error={resource.pageError} retry={() => void resource.next()} busy={resource.loading} />
      )}
      {resource.data && (items.length > 0 || resource.pageNumber > 1 || resource.hasNext) && (
        <Pagination label="研究用量分页" {...resource} />
      )}
    </>
  );
}
function BillingModule({
  location,
  navigate,
  revision,
}: {
  location: AccountLocation;
  navigate: (patch: Partial<AccountLocation>) => void;
  revision: number;
}) {
  let filter: RecordsFilter = {
      type: location.type,
      research_id: location.researchId,
      record_id: location.recordId,
    },
    error = "";
  try {
    filter = { ...filter, ...accountDateFilter(location) };
  } catch (e) {
    error = e instanceof Error ? e.message : "请选择日期";
  }
  return (
    <section className="auth-glass acct-card acct-billing-card" aria-label="研值明细">
      <div
        className="acct-billing-tabs"
        role="tablist"
        aria-label="研值明细视图"
        onKeyDown={(event) => {
          let next: AccountLocation["billingView"];
          if (event.key === "ArrowLeft" || event.key === "ArrowRight")
            next = location.billingView === "records" ? "research" : "records";
          else if (event.key === "Home") next = "records";
          else if (event.key === "End") next = "research";
          else return;
          event.preventDefault();
          navigate({ billingView: next });
          event.currentTarget
            .querySelector<HTMLButtonElement>(`#account-billing-${next}-tab`)
            ?.focus();
        }}
      >
        {([
          ["records", "流水", ReceiptText],
          ["research", "研究", FlaskConical],
        ] as const).map(([view, label, Icon]) => (
          <button
            key={view}
            id={`account-billing-${view}-tab`}
            type="button"
            role="tab"
            aria-selected={location.billingView === view}
            aria-controls={`account-billing-${view}-panel`}
            tabIndex={location.billingView === view ? 0 : -1}
            onClick={() => {
              if (location.billingView !== view) navigate({ billingView: view });
            }}
          >
            <Icon size={16} aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>
      {location.billingView === "records" ? (
        <div
          id="account-billing-records-panel"
          role="tabpanel"
          aria-labelledby="account-billing-records-tab"
          tabIndex={0}
        >
          <div className="acct-filters">
            <label>
              时间范围
              <select
                aria-label="时间范围"
                value={location.range}
                onChange={(e) =>
                  navigate({
                    range: e.target.value as AccountLocation["range"],
                    recordId: "",
                  })
                }
              >
                <option value="7">最近 7 天</option>
                <option value="30">最近 30 天</option>
                <option value="all">全部时间</option>
                <option value="custom">自定义日期</option>
              </select>
            </label>
            <label>
              变动类型
              <select
                aria-label="变动类型"
                value={location.type}
                onChange={(e) => navigate({ type: e.target.value, recordId: "" })}
              >
                <option value="">全部类型</option>
                {[
                  "usage",
                  "redeem",
                  "purchase",
                  "refund",
                  "checkin",
                  "settle",
                  "reconciliation",
                ].map((type) => (
                  <option key={type} value={type}>
                    {recordTypeLabel(type)}
                  </option>
                ))}
              </select>
            </label>
            {location.range === "custom" && (
              <>
                <label>
                  开始日期
                  <input
                    type="date"
                    aria-label="开始日期"
                    value={location.start}
                    onChange={(e) =>
                      navigate({ start: e.target.value, recordId: "" })
                    }
                  />
                </label>
                <label>
                  结束日期
                  <input
                    type="date"
                    aria-label="结束日期"
                    value={location.end}
                    onChange={(e) =>
                      navigate({ end: e.target.value, recordId: "" })
                    }
                  />
                </label>
              </>
            )}
          </div>
          {location.recordId && (
            <div className="acct-filter-tag">
              正在查看指定流水
              <button
                type="button"
                className="acct-text-link"
                onClick={() =>
                  navigate({
                    recordId: "",
                    type: "",
                    researchId: "",
                    range: "all",
                  })
                }
              >
                查看全部流水
              </button>
            </div>
          )}
          {location.researchId && (
            <div className="acct-filter-tag">
              研究筛选：
              <CopyText value={location.researchId} label={location.researchId} />
              <button
                type="button"
                className="acct-text-link"
                onClick={() => navigate({ researchId: "", recordId: "" })}
              >
                清除研究筛选
              </button>
            </div>
          )}
          {error ? (
            <p className="auth-error" role="alert">
              {error}
            </p>
          ) : (
            <RecordsList
              key={JSON.stringify(filter)}
              filter={filter}
              revision={revision}
              recordId={location.recordId}
            />
          )}
        </div>
      ) : (
        <div
          id="account-billing-research-panel"
          role="tabpanel"
          aria-labelledby="account-billing-research-tab"
          tabIndex={0}
        >
          <ResearchUsage
            onResearch={(id) =>
              navigate({
                billingView: "records",
                researchId: id,
                range: "all",
                type: "",
                recordId: "",
              })
            }
          />
        </div>
      )}
      <Feedback topic="研值明细" />
    </section>
  );
}
function RedeemModule({
  credits,
  onRedeemed,
  viewRecord,
}: {
  credits: CreditsResponse | null;
  onRedeemed: (result: RedeemSuccess) => void;
  viewRecord: (id: string) => void;
}) {
  const redeemer = useRef(createRedeemer());
  const [code, setCode] = useState(""),
    [busy, setBusy] = useState(false),
    [outcome, setOutcome] = useState<RedeemOutcome | null>(null),
    [cooldown, setCooldown] = useState(0),
    [revision, setRevision] = useState(0);
  const controller = useRef(new AbortController());
  useEffect(() => {
    const c = new AbortController();
    controller.current = c;
    return () => c.abort();
  }, []);
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(
      () => setCooldown((v) => Math.max(0, v - 1)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [cooldown > 0]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || cooldown) return;
    if (!code.trim()) {
      setOutcome({ kind: "failure", message: "请输入兑换码" });
      return;
    }
    setBusy(true);
    setOutcome(null);
    const signal = controller.current.signal;
    const result = await redeemer.current.redeem(code, signal);
    if (signal.aborted) return;
    setBusy(false);
    setOutcome(result);
    if (result.kind === "success") {
      setCode("");
      onRedeemed(result);
      setRevision((v) => v + 1);
    } else setCooldown(result.retryAfterSeconds ?? 0);
  };
  return (
    <>
      <Card
        icon={Ticket}
        title="使用兑换码"
        desc={
          credits?.unlimited
            ? "当前账号为无限额度账号，无需兑换研值。"
            : "输入兑换码领取研值。"
        }
      >
        <form
          className="acct-redeem-form"
          noValidate
          aria-busy={busy}
          onSubmit={(e) => void submit(e)}
        >
          <label className="sr-only" htmlFor="account-redeem-code">
            兑换码
          </label>
          <input
            id="account-redeem-code"
            autoComplete="off"
            spellCheck={false}
            value={code}
            placeholder="请输入兑换码"
            aria-describedby={outcome?.kind === "failure" ? "account-redeem-error" : undefined}
            disabled={!!credits?.unlimited || busy}
            onChange={(e) => {
              setCode(e.target.value);
              setOutcome(null);
            }}
          />
          <button
            type="submit"
            className="auth-primary"
            disabled={busy || cooldown > 0 || !!credits?.unlimited}
          >
            {busy ? "领取中…" : cooldown ? `${cooldown}s 后重试` : "领取研值"}
          </button>
        </form>
        {outcome?.kind === "failure" && (
          <p className="auth-error acct-redeem-error" id="account-redeem-error" role="alert">
            {outcome.message}
          </p>
        )}
        {outcome?.kind === "success" && (
          <div className="acct-redeem-ok" role="status">
            <div className="acct-redeem-ok-head">
              <CheckCircle2 size={18} />
              <p>兑换成功</p>
            </div>
            <dl>
              <div>
                <dt>活动名称</dt>
                <dd>{outcome.campaignName}</dd>
              </div>
              <div>
                <dt>本次获得</dt>
                <dd>+{formatCredits(outcome.rewardCredits)} 研值</dd>
              </div>
              <div>
                <dt>兑换后余额</dt>
                <dd>{formatCredits(outcome.balance)}</dd>
              </div>
              <div>
                <dt>兑换时间</dt>
                <dd>{formatDateTime(outcome.redeemedAt)}</dd>
              </div>
            </dl>
            <button
              className="acct-text-link"
              type="button"
              onClick={() => viewRecord(outcome.record.id)}
            >
              查看本次流水 →
            </button>
          </div>
        )}
        <Feedback topic="兑换问题" />
      </Card>
      <Card
        icon={History}
        title="兑换记录"
      >
        <RecordsList key={revision} filter={{ type: "redeem" }} variant="redeem" />
      </Card>
    </>
  );
}
export function RedeemPage() {
  const { status, user } = useSession();
  return (
    <Stage>
      <p className="auth-kicker">Redeem</p>
      <h1 className="acct-title">兑换研值</h1>
      {status === "authenticated" && user ? (
        <StandaloneRedeem
          key={`${user.id}:${tokenStore.getSessionVersion()}`}
        />
      ) : (
        <SessionGate />
      )}
    </Stage>
  );
}
function StandaloneRedeem() {
  const reconciled = useRef<CreditsResponse | null>(null);
  const credits = useResource(
    (signal) =>
      fetchCredits(signal).then((data) => {
        signal.throwIfAborted();
        const next = newerCredits(reconciled.current, data);
        reconciled.current = next;
        return next;
      }),
    [],
  );
  return (
    <div className="acct-content acct-solo">
      {credits.error && (
        <Failure error="余额暂时无法更新，请重试。" retry={credits.reload} />
      )}
      <RedeemModule
        credits={credits.data}
        onRedeemed={(result) => {
          const next = newerCredits(reconciled.current, {
            balance: result.balance,
            balance_revision: result.balanceRevision,
            unlimited: false,
          });
          reconciled.current = next;
          credits.update(next);
          credits.reload();
        }}
        viewRecord={(id) => {
          window.location.href = `/account?tab=billing&range=all&type=redeem&record_id=${encodeURIComponent(id)}`;
        }}
      />
      <a className="acct-text-link" href="/account">
        前往个人中心 <ArrowRight size={15} />
      </a>
    </div>
  );
}
